import { FC, useMemo, useEffect } from "react";
import { useTranslation } from "@vixi/localization";
import { CourseJobStatus, COURSE_STATUS } from "@vixi/types";
import { useCourseJobs } from "@/hooks/api/course/useCourseJobs";
import { useNavigate, useLocation } from "@vixi/router";
import { allCoursesQueryOptions } from "@vixi/api";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@vixi/ui";

interface InProgressJobCardProps {
  job: CourseJobStatus;
  onClick: () => void;
}

const InProgressJobCard: FC<InProgressJobCardProps> = ({ job, onClick }) => {
  const { t } = useTranslation();
  
  // Show all jobs regardless of completion status (even if 100%)
  // This allows users to see progress and continue from where they left off
  const progressPercentage = job.progressPercentage ?? 0;
  const progressMessage = job.progressMessage || t("LoadingMessage.CourseInMaking");

  return (
    <div className="bg-card flex flex-col rounded border-2 border-transparent p-4 transition-all duration-300">
      {/* Progress Bar */}
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">
            {Math.round(progressPercentage)}%
          </span>
          {job.totalPages > 0 && (
            <span className="text-xs text-muted-foreground">
              {job.totalPages} {t("Common.Pages")}
            </span>
          )}
        </div>
        {/* Custom Progress Bar */}
        <div className="bg-paper relative h-2 w-full overflow-hidden rounded">
          <div
            className="bg-primary h-full rounded transition-all duration-300"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Progress Message */}
      <p className="text-sm text-foreground-secondary mb-3 line-clamp-2">
        {progressMessage}
      </p>

      {/* Job Info */}
      <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>{job.language.toUpperCase()}</span>
        <span>
          {new Date(job.createdAt).toLocaleDateString()}
        </span>
      </div>

      {/* Continue Button */}
      <Button
        variant="contained"
        size="small"
        onClick={(e) => {
          e.stopPropagation(); // Prevent any parent click handlers
          onClick();
        }}
        className="w-full"
      >
        {t("Action.Continue") || "Continue"}
      </Button>
    </div>
  );
};

export const InProgressCoursesSection: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: jobsData, isLoading, isError, error } = useCourseJobs();

  // Fetch all courses to check if any course is saved (In Review or Published)
  // This helps us determine if a job's course is already saved in database
  // Refetch every 30 seconds to catch newly saved courses (not too frequent to avoid performance issues)
  const { data: coursesData } = useQuery({
    ...allCoursesQueryOptions({ 
      pageNumber: 1, 
      pageSize: 1000 // Get all courses to check
    }),
    refetchInterval: 30000, // Refetch every 30 seconds to catch newly saved courses
  });
  
  // Create a set of saved course identifiers to quickly check if a course exists
  // We match courses by eBookAbout, targetAudience, and conversationTone
  const savedCoursesSet = useMemo(() => {
    if (!coursesData?.items) return new Set<string>();
    
    return new Set(
      coursesData.items
        .filter(course => 
          course.courseStatus === COURSE_STATUS.WAITING_PUBLISH || 
          course.courseStatus === COURSE_STATUS.PUBLISHED
        )
        .map(course => {
          // Use course content as identifier (eBookAbout, targetAudience, conversationTone)
          // Normalize whitespace to ensure consistent matching
          const identifier = `${course.eBookAbout || ''}-${course.targetAudience || ''}-${course.conversationTone || ''}`;
          return identifier.toLowerCase().replace(/\s+/g, ' ').trim();
        })
        .filter(id => id.length > 0) // Filter out empty identifiers
    );
  }, [coursesData]);

  // Clean up localStorage entries ONLY for courses that are already saved in database
  // IMPORTANT: Do NOT remove metadata for jobs that don't exist in API response
  // because user might have closed the tab and come back later - the job still exists on server
  // Only remove metadata when course is confirmed saved (In Review or Published)
  useEffect(() => {
    const JOB_COURSE_DATA_STORAGE_KEY = "course-generation-job-course-data";
    const keysToRemove: string[] = [];
    
    // Only clean up if we have courses data (to check if course is saved)
    // Don't remove metadata based on jobsData alone - jobs might not be loaded yet
    if (!coursesData?.items || savedCoursesSet.size === 0) {
      // If no courses data, don't remove anything
      // This prevents deletion when user changes tab/route before courses are loaded
      return;
    }
    
    // Get all jobIds from API response (for logging only, not for deletion)
    const validJobIds = new Set(jobsData?.items?.map(job => job.jobId) || []);
    
    // Check all localStorage entries for course metadata
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${JOB_COURSE_DATA_STORAGE_KEY}-`)) {
        try {
          const storedData = localStorage.getItem(key);
          if (!storedData) continue;
          
          const parsed = JSON.parse(storedData);
          const storedIdentifier = `${parsed.eBookAbout || ''}-${parsed.targetAudience || ''}-${parsed.conversationTone || ''}`.toLowerCase().replace(/\s+/g, ' ').trim();
          
          // ONLY remove if course is confirmed saved in database (In Review or Published)
          // Do NOT remove based on jobId existence - user might return later
          if (storedIdentifier && storedIdentifier.length > 0 && savedCoursesSet.has(storedIdentifier)) {
            keysToRemove.push(key);
            console.log(`🗑️ Removing metadata for saved course: ${key}`);
          }
        } catch (error) {
          // Skip invalid entries
          continue;
        }
      }
    }
    
    // Remove only entries for courses that are confirmed saved
    if (keysToRemove.length > 0) {
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        console.log(`✅ Removed metadata key: ${key}`);
      });
    }
  }, [coursesData, savedCoursesSet]); // Removed jobsData from dependencies - don't delete based on jobs

  // CourseGenerationJobStatus enum values
  const JOB_STATUS = {
    Queued: 0,
    Processing: 1,
    Completed: 2,
    Failed: 3,
    Cancelled: 4,
  } as const;

  // Filter jobs that are in progress OR completed but need to continue to next step
  // Hide: Jobs that have been saved (course exists in database OR temp-active-job-id was cleared after save)
  const inProgressJobs = useMemo(() => {
    if (!jobsData?.items) return [];
    
    const TEMP_ACTIVE_JOB_ID_KEY = "temp-active-job-id";
    const JOB_COURSE_DATA_STORAGE_KEY = "course-generation-job-course-data";
    
    // Get temp active jobId (if user clicked on a card and is currently working on it)
    let tempActiveJobId: string | null = null;
    try {
      tempActiveJobId = localStorage.getItem(TEMP_ACTIVE_JOB_ID_KEY);
    } catch (error) {
      // Ignore errors
    }
    
    return jobsData.items.filter(
      (job) => {
        // Convert status to number if it's a string (just in case API returns string)
        const status = typeof job.status === 'string' ? parseInt(job.status, 10) : job.status;
        
        // For Queued, Processing, Failed, Cancelled - always show
        if (status === JOB_STATUS.Queued || 
            status === JOB_STATUS.Processing || 
            status === JOB_STATUS.Failed || 
            status === JOB_STATUS.Cancelled) {
          return true;
        }
        
        // For Completed jobs (status = 2) - check if course is saved in database
        if (status === JOB_STATUS.Completed) {
          // Logic:
          // 1. If no metadata in localStorage → Hide card (metadata was removed after save)
          // 2. If metadata exists and course is saved in database → Hide card
          // 3. If metadata exists but course not saved → Show card (user needs to complete steps)
          
          const metadataKey = `${JOB_COURSE_DATA_STORAGE_KEY}-${job.jobId}`;
          const hasMetadata = !!localStorage.getItem(metadataKey);
          
          // If no metadata, card was already removed after save - hide it
          if (!hasMetadata) {
            console.log(`🗑️ Hiding job ${job.jobId} - metadata was removed (course saved)`);
            return false;
          }
          
          // If we have saved courses, try to match by metadata
          if (savedCoursesSet.size > 0 && coursesData?.items && coursesData.items.length > 0) {
            try {
              const storedData = localStorage.getItem(metadataKey);
              if (storedData) {
                const jobCourseMetadata = JSON.parse(storedData);
                // Normalize whitespace to ensure consistent matching
                const jobIdentifier = `${jobCourseMetadata.eBookAbout || ''}-${jobCourseMetadata.targetAudience || ''}-${jobCourseMetadata.conversationTone || ''}`.toLowerCase().replace(/\s+/g, ' ').trim();
                
                // If course is saved (exists in savedCoursesSet), hide the job card
                if (jobIdentifier && jobIdentifier.length > 0 && savedCoursesSet.has(jobIdentifier)) {
                  // Course is saved - remove from localStorage and hide card
                  try {
                    localStorage.removeItem(metadataKey);
                    console.log(`🗑️ Removed metadata and hiding job ${job.jobId} - course saved in database`);
                  } catch (error) {
                    console.warn("Failed to remove job course metadata:", error);
                  }
                  return false;
                }
              }
            } catch (error) {
              // If parsing fails, continue without metadata
              console.warn("Failed to parse job course metadata:", error);
            }
          }
          
          // If metadata exists and course not found in DB, show the job
          // User needs to complete the steps and save the course
          return true;
        }
        
        // Default: show the job
        return true;
      }
    );
  }, [jobsData, savedCoursesSet]);

  // Don't show card on course creation flow pages (to avoid conflicts)
  const currentPath = location.pathname || "";
  const isOnFlowPage = currentPath.includes("/create/ebook/flow") || currentPath.includes("/create/linkedin/flow");
  
  if (isOnFlowPage) {
    return null;
  }

  // Don't show card if error (fail silently for better UX)
  if (isError) {
    console.error("Failed to load in-progress courses:", error);
    return null;
  }

  // Don't show card if no jobs
  if (!isLoading && inProgressJobs.length === 0) {
    return null;
  }

  const handleJobClick = (jobId: string, job: CourseJobStatus) => {
    // Save active jobId to localStorage (temp-active-job-id)
    // This will be used to remove the card when course is saved
    const TEMP_ACTIVE_JOB_ID_KEY = "temp-active-job-id";
    try {
      localStorage.setItem(TEMP_ACTIVE_JOB_ID_KEY, jobId);
      console.log(`💾 Saved temp active jobId: ${jobId}`);
    } catch (error) {
      console.warn("Failed to save temp active jobId:", error);
    }
    
    // Always pass jobId so we can load course data from result
    // Even if progress = 100%, we need jobId to fetch the result data
    navigate({
      to: "/create/ebook/flow",
      search: { jobId },
    });
  };

  // Fixed card at bottom right of all pages
  return (
    <div className="fixed bottom-4 end-4 z-50 w-80 max-w-[calc(100vw-2rem)]">
      <div className="bg-card border-primary rounded-lg border-2 p-4 shadow-lg">
        {/* Card Title */}
        <h3 className="mb-3 text-sm font-semibold text-foreground">
          {t("Dashboard.InProgressCourses")}
        </h3>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-paper h-24 animate-pulse rounded border-2 border-transparent"
              />
            ))}
          </div>
        )}

        {/* Jobs List - Show all jobs including those at 100% */}
        {!isLoading && inProgressJobs.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {inProgressJobs.map((job) => (
              <InProgressJobCard
                key={job.jobId}
                job={job}
                onClick={() => handleJobClick(job.jobId, job)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
